/* eslint-disable no-underscore-dangle */

import { MISSING_DEVICE_MARKER } from '@sb/integrations/implementations/gripper-general/steps/ActuateGripper/Arguments';
import { isForceSensor } from '@sb/integrations/utils';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepPlayArguments } from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';

import { ZeroForceTorqueSensorArguments as Arguments } from './Arguments';
import { ZeroForceTorqueSensorVariables as Variables } from './Variables';

export class ZeroForceTorqueSensorStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      succeeded: false,
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    const { command, deviceID } = this.args;

    this.setVariable('succeeded', false);

    const equipmentState = this.routineContext.equipment.getEquipmentState();

    const enabledSensor = equipmentState.find((item) =>
      isForceSensor(item.kind),
    );

    if (enabledSensor == null) {
      const message = `No enabled force/torque sensor found. Enable a force/torque sensor in the Equipment Manager to zero it.`;

      return fail({
        failure: {
          kind: FailureKind.GripperFailure,
          message,
        },
        failureReason: message,
      });
    }

    // Guard against the selection drifting from what is actually enabled (e.g.
    // the sensor was swapped after the step was configured).
    // MISSING_DEVICE_MARKER means a cross-robot import couldn't remap the
    // selection (see sanitizeEquipmentReferences); fall through to the enabled
    // sensor instead of failing, matching ActuateGripperStep.
    if (
      deviceID &&
      deviceID !== MISSING_DEVICE_MARKER &&
      enabledSensor.id !== deviceID
    ) {
      const enabledDevice = this.routineContext.equipment.getDeviceById(
        enabledSensor.id,
      );

      const selectedDevice =
        this.routineContext.equipment.getDeviceById(deviceID);

      const enabledName = enabledDevice?.getConfig().name;
      const selectedName = selectedDevice?.getConfig().name;

      const enabledPart = enabledName ? ` "${enabledName}"` : '';
      const selectedPart = selectedName ? ` "${selectedName}"` : '';

      const message = `The enabled force/torque sensor${enabledPart} does not match the selected sensor${selectedPart}. Enable the correct sensor in the Equipment Manager.`;

      return fail({
        failure: {
          kind: FailureKind.GripperFailure,
          message,
        },
        failureReason: message,
      });
    }

    try {
      await this.routineContext.actuateDevice(command);
    } catch (error) {
      const message = `Failed to zero force/torque sensor: ${error.message}`;

      return fail({
        failure: {
          kind: FailureKind.GripperFailure,
          message,
        },
        failureReason: message,
        error,
      });
    }

    this.setVariable('succeeded', true);

    return undefined;
  }
}
